import { currencySymbol } from '../utils/currencies'
import { updateObjectInList } from 'cogsworth/utils/immutability'
import { staffServices, serviceStaffs } from 'cogsworth/utils/staffs'
import { getTimeFilter } from 'cogsworth/utils/filters'
import { getAnswersFromUrl, getOptionsFromUrl } from 'cogsworth/utils/filters'
import { serviceCategoriesFor } from './services'
import { getUrlParameter } from './filters'

const customerFieldsIds = ['name', 'email']
const ANY_STAFF_VALUE = 'any'
const DEFAULT_QUESTIONS = {
  "booking-service-category-question": "Choose a service category",
  "booking-service-question": "Which service would you like to book?",
  "booking-duration-question": "Choose a duration for your appointment",
  "booking-staff-question": "Would you prefer a staff member in particular?",
  "booking-date-question": "Choose a date for your appointment",
  "booking-time-question": "Choose a time for your appointment",
  "booking-time-confirm-question": "Confirm your selection",
  "booking-location-question": "Choose a location for your appointment",
  "booking-guests-question": "Add guests to this appointment",
}
const VIRTUAL_LOCATION_TYPES = ["URL", "ZOOM_MEETING", "GOOGLE_HANGOUTS_URL", "TEAMS_MEETING"]

export function addVariablesToField(field){
  if( /\$\{(.+?)\}/ig.test(field.question) ) {
    let regex = /\$\{(.+?)\}/ig
    let variables = []
    let match = null
    while (( match = regex.exec(field.question) )) {
      variables.push(match[1])
    }
    return { ...field, variables }
  }
  return field
}

export function getBookingFields({ id, business, appointment, pageType, answers, locales, setError }) {
  return [
    getServiceCategoryField({ id, business, pageType, locales }),
    getServiceField({ id, business, appointment, pageType, locales, order: business.servicesOrder, setError, answers }),
    getStaffField({ id, business, appointment, pageType, locales, answers }),
    getDurationField({ id, business, appointment, pageType, answers, locales }),
    getBookingDateField({ business }),
    getLocationField({ business, appointment, answers, pageType }),
  ]
}

export function getEndBookingFields({ business, appointment, answers }) {
  return [
    getGuestsField({ business, appointment, answers }),
  ]
}

const getBookingDateField = ({ business }) => {
  let field = {
    id: 'booking_date',
    type: 'date',
    question: getBookingFieldQuestion("booking-date-question", business)
  }

  // Pre-set timeslot with url filter
  const preselectedTimeslot = getTimeFilter(window.location.search)
  if (preselectedTimeslot) {
    field.value = preselectedTimeslot
    field.hidden = true
  }

  return field
}

export function getBookingFieldQuestion(questionId, business) {
  const bookingField = business.bookingForm && business.bookingForm.fields.find(f => f.id === "booking" || f.id === "booking_0")
  if(bookingField && bookingField[questionId]) {
    return bookingField[questionId].replace(/&nbsp;/gi, ' ').replace(/<.+?>|\\n/gi, '')
  }
  return DEFAULT_QUESTIONS[questionId]
}

function getActiveServices(services) {
  return services.filter(service => {
    if (service.enabled === false || service.deleted) {
      return false
    }

    if (service.staffs.length === 0) {
      return true
    }

    const activeStaffs = service.staffs.filter(s => s.user)
    if (activeStaffs.length === 0) {
      return false
    }

    return true
  })
}

function getServiceCategoryField({ id, business, pageType, locales }) {
  let activeServices = getActiveServices(business.services)
  activeServices = activeServices.filter(s => !s.adHocService)

  let field = {
    id: 'booking_service_category',
    type: 'select',
    question: getBookingFieldQuestion("booking-service-category-question", business),
    options: serviceCategoriesFor(activeServices, business.serviceCategories, { locales })
  }

  switch (pageType) {
    case 'staff':
      activeServices = staffServices(activeServices, id)
      field.options = serviceCategoriesFor(activeServices, business.serviceCategories, { locales })
      break
    case 'service':
    case 'adhocservice':
    case 'appointment':
      field.value = null
      field.hidden = true
      break
    default:
      field.hidden = false
      const filteredCategoryId = getUrlParameter(window.location.search, 'filter[category]')
      const category = field.options.find(o => o.id === filteredCategoryId)
      if (category) {
        field.value = category.id
        field.hidden = true
      }
  }

  if (field.options.length < 2) {
    field.hidden = true
    field.value = null
  }

  return field
}

function getServiceField({ id, business, appointment, pageType, locles, order, setError, answers }) {
  let activeServices = getActiveServices(business.services)
  activeServices = activeServices.filter(s => !s.adHocService) // Filter out adhoc services

  if (pageType === 'business') {
    // Filter out any services whose visibility is set to SELF (only render from its own endpoint)
    activeServices = activeServices.filter(s => s.bookingPageVisibility !== 'SELF')
  }

  let field = {
    id: 'booking_service',
    type: 'select',
    question: getBookingFieldQuestion("booking-service-question", business),
    options: activeServices,
    required: true,
  }

  switch (pageType) {
    case 'service':
      field.value = id
      field.hidden = true
      break
    case 'adhocservice':
      const service = field.value = business.services.find(s => s.adHocService && s.adHocService.id === id)
      field.value = service.id
      field.hidden = true
      break
    case 'staff':
      const staff = business.staffs.find(s => s.id === id)
      field.options = staffServices(activeServices, staff.id)

      const filteredServiceId = getUrlParameter(window.location.search, 'filter[service]')
      const filteredService = field.options.find(o => o.id === filteredServiceId)
      if (filteredService) {
        field.value = filteredService.id
        field.hidden = true
      }

      if (field.options.length === 0 && setError) {
        setError('no-staff-services')
      }

      if (field.options.length === 1) {
        field.value = field.options[0].id
        field.hidden = true
      }
      break
    case 'appointment':
      if (appointment && appointment.service) {
        field.value = appointment.service.id
        field.hidden = true
      }
      break
    default:
      break
  }

  let options = orderedServices(field.options, order)

  if (answers.booking_service_category === "other") {
    options = options.filter(s => s.categories.length === 0)
  } else if (answers.booking_service_category) {
    options = options.filter(s => s.categories.some(c => c.id === answers.booking_service_category))
  }

  setMetaDescription(options, field.value)

  if (!field.value && options.length === 1 && !business.forceServiceStep) {
    field.value = options[0].id
    field.hidden = true
  }

  return {
    ...field,
    options,
  }
}

const setMetaDescription = (services, selectedService) => {
  if(services.length === 0) {
    return
  }

  let service = services.find(s => s.id === selectedService)
  if(!service) {
    service = services[0]
  }

  let description = service.name
  if(service.description) {
    description = description + ' - ' + service.description
  }
  document.querySelector('meta[name="description"]').setAttribute("content", description)
}

const orderedServices = (services, order) => {
  if(!order || order.length === 0) {
    return services
  }

  return services.sort((a, b) => {
    if(order.indexOf(a.id) > order.indexOf(b.id)){
      return 1
    } else {
      return -1
    }
  })
}

const staffOptionsFor = ({ business, staffs, locales }) => {
  let options =  []
  if (business.allowNoStaffPreference !== false) {
    options.push({ id: ANY_STAFF_VALUE, name: locales['no-preference'] })
  }
  return [ ...options, ...staffs ]
}

function getStaffField({ id, business, appointment, pageType, locales, answers }) {
  const activeServices = getActiveServices(business.services)
  const selectedService = activeServices.find(s => s.id === answers['booking_service'])

  let field = {
    id: 'booking_staff',
    type: 'select',
    question: getBookingFieldQuestion("booking-staff-question", business),
    options: staffOptionsFor({
      business,
      staffs: serviceStaffs({ service: selectedService, business }),
      locales,
    }),
  }

  switch (pageType) {
    case 'staff':
      field.value = id
      field.hidden = true
      break
    case 'service':
      const service = business.services.find(s => s.id === id)
      if (service.adHocService && service.adHocService.id && service.staffs[0]) {
        service.staffs[0].enabled = true // We don't care if staff is enabled or not for adhoc meetings
      }
      const staffs = serviceStaffs({ service, business })
      field.options = staffOptionsFor({
        business,
        staffs,
        locales,
      })
      if (field.options.filter(o => o.id !== ANY_STAFF_VALUE).length === 1) {
        field.value = field.options[0].id
        field.hidden = true
      }
      break
    case 'adhocservice':
      const adhocserviceService = business.services.find(s => s.adHocService && s.adHocService.id === id)
      field.value = adhocserviceService.staffs[0].id
      field.hidden = true
      break
    case 'appointment':
      const appointmentStaff = field.options.find(o => o.id === appointment.staff.id)
      if (appointmentStaff && !answers['booking_staff']) {
        field.value = appointmentStaff.id
      }
      if(appointment.adHocService) {
        field.hidden = true
      }
      break
    default:
      if (field.options.filter(o => o.id !== ANY_STAFF_VALUE).length === 1) {
        field.value = field.options[0].id
        field.hidden = true
      }
      break
  }

  field.onChange = (value, _fields) => {
    return new Promise((resolve, reject) => {
      resolve(updateObjectInList(_fields, 'id', 'booking_date', {
        value: null
      }))
    })
  }

  // If business doesn't allow manually selecting staff and we are not on a Staff booking page, set it to "ANY" and hide the field.
  if (!business.allowChoosingStaff && pageType !== 'staff') {
    field.value = ANY_STAFF_VALUE
    field.hidden = true
  }

  return field
}

export function getFieldQuestion(field, fields){
  let question = field.question
  if(field.variables && field.variables.length) {
    field.variables.forEach((variable) => {
      let variableField = fields.find(v => v.id === variable)
      if( variableField && variableField.value ) {
        question = question.replace('${' + variable + '}', variableField.value)
      } else {
        question = question.replace('${' + variable + '}', '')
      }
    })
  }
  return question.replace(/&nbsp;/gi, ' ').replace(/<.+?>|\\n/gi, '')
}

function getDurationFieldOptions(service, locales) {
  return service.durations.map((duration) => {
    const hours = Math.floor(duration.duration / 60)
    const minutes = duration.duration % 60

    let label = ``
    if (hours > 0) {
      label = `${hours} ${hours === 1 ? 'hour' : 'hours'}`
    }
    if (hours > 0 && minutes > 0) {
      label += ` `
    }
    if (minutes > 0) {
      label += `${minutes} ${locales['minutes']}`
    }

    if(duration.price > 0) {
      label = label + ` - ${currencySymbol(service.currency)} ${duration.price}`
    }

    return {
      id: duration.duration,
      name: label
    }
  })
}

function getDurationField({ id, business, appointment, pageType, answers, locales }) {
  let field = {
    id: 'booking_duration',
    type: 'select',
    question: getBookingFieldQuestion("booking-duration-question", business),
    options: []
  }

  const activeServices = getActiveServices(business.services)
  let service = null

  // Get service from state
  // TODO: The service should really always come from the persisted state to reduce business logic duplication
  switch(pageType){
    case 'service':
      service = business.services.find(s => s.id === id)
      break
    case 'adhocservice':
      const adhocserviceService = business.services.find(s => s.adHocService && s.adHocService.id === id)
      field.value = adhocserviceService.durations[0].duration
      field.hidden = true
      break
    case 'appointment':
      service = activeServices.find(s => s.id === appointment.service.id)
      break
    default:
      service = activeServices.find(s => s.id === answers['booking_service'])
      break
  }

  if (service) {
    field.question = getBookingFieldQuestion("booking-duration-question", business)
    field.options = getDurationFieldOptions(service, locales)

    // If the
    if (field.options.length === 1 && field.value === undefined) {
      field.value = field.options[0].id
      field.hidden = true
    } else if (appointment && field.value === undefined) {
      const appointmentDuration = field.options.find(o => o.id === appointment.duration)
      if (appointmentDuration) {
        field.value = appointmentDuration.id
      }
    }
  }

  if (field.options.length === 0) {
    field.hidden = true
  }

  return field
}

export const getLocation = ({ business, service, appointment, answers }) => {
  if (appointment) {
    return {
      locationType: appointment.locationType,
      locationValue: appointment.locationValue,
    }
  }

  if (!service) {
    return {}
  }

  const staffs = serviceStaffs({ service, business })
  const staffIsSelected = answers.booking_staff && answers.booking_staff !== 'any'

  let selectedStaff = staffIsSelected ? staffs.find(s => s.id === answers.booking_staff) : null
  if (answers.booking_staff === 'any' && staffs.length === 1) {
    selectedStaff = staffs[0]
  }

  const overrideStaffLocation = service.locationOverridesStaffs || service.adHocService

  // If both the service and staff have a virtual location, use staff's location
  const serviceIsVirtual = !service.locationType || service.locationType === "NONE" || VIRTUAL_LOCATION_TYPES.indexOf(service.locationType) > -1
  const staffIsVirtual = selectedStaff && VIRTUAL_LOCATION_TYPES.indexOf(selectedStaff.locationType) > -1

  if (serviceIsVirtual && staffIsVirtual && !overrideStaffLocation) {
    return {
      locationType: selectedStaff.locationType,
      locationValue: selectedStaff.locationValue,
    }
  }

  return {
    locationType: service.locationType,
    locationValue: service.locationValue,
  }
}

export function getLocationField({ business, appointment, answers, pageType }) {
  let field = {
    id: 'booking_location',
    type: 'text',
    question: getBookingFieldQuestion("booking-location-question", business),
    hidden: true,
    required: true,
  }

  const service = getService({ business, answers, pageType })
  const { locationType, locationValue } = getLocation({ business, service, appointment, answers })

  if (locationType === 'SET_BY_CUSTOMER' || locationType === 'SET_BY_CUSTOMER_VIRTUAL') {
    field.hidden = false
  } else if (locationType === 'SET_BY_CUSTOMER_GOOGLE_MAPS_ADDRESS') {
    field.hidden = false
    field.type = 'google-maps-address'
  }

  field.value = locationValue

  return field
}

export function getGuestsField({ business, answers, appointment }) {
  let field = {
    id: 'booking_guests',
    type: 'guests',
    question: getBookingFieldQuestion("booking-guests-question", business),
    hidden: true,
  }

  const activeServices = getActiveServices(business.services)
  const selectedService = activeServices.find(s => s.id === answers['booking_service'])

  if (selectedService && selectedService.allowGuests && !appointment) {
    field.hidden = false
  }

  return field
}

const getService = ({ business, answers, pageType }) => {
  const activeServices = pageType === 'service' ? business.services : getActiveServices(business.services)
  return activeServices.find(s => s.id === answers.booking_service)
}

export function getCustomFields({ id, business, pageType, features, isInitialSetup }) {
  // If we are rescheduling an appointment, we don't display the custom fields
  if(pageType === 'appointment') return []

  const urlAnswers = getAnswersFromUrl()
  const urlOptions = getOptionsFromUrl()

  return business.bookingForm.fields.filter(f => f.id !== 'booking' && f.id !== 'booking_0').reduce((acc, field) => {
    // If custom booking form is disabled by features, only return mandatory customer fields
    if( !features.customBookingForm && !customerFieldsIds.find(id => field.id === id) ) {
      return acc
    }

    let options = null
    if (field.options && field.options.length > 0) {
      options = field.options.map(option => ({ id: option, name: option }))
    }
    if (field.otherField) {
      options.push({
        id: "__other",
        name: field.otherFieldLabel || "Other:",
        isOtherField: true,
      })
    }

    let customField = {
      ...addVariablesToField(field),
      options,
      isMultiple: field.isMultiple,
    }

    if (isInitialSetup && urlAnswers[field.id] && features.prefilledBookingAnswers) {
      customField.value = urlAnswers[field.id]
    }
    if (urlAnswers[field.id] && urlOptions.edit_answers !== 'true') {
      customField.hidden = true
    }

    acc.push(customField)

    return acc
  }, [])
}
